import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import AlertModal from "../../Requirement/components/common/AlerModel";

const CompanyInformation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: "",
    companyEmail: "",
    companyWebsite: "",
    companyCountry: "",
    companyState: "",
    companyZipCode: "",
    companyPhone1: "",
    companyPhone2: "",
    companyAddress1: "",
    companyAddress2: "",
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [activeUser, setActiveUser] = useState("");
  const [userInfo, setUserInfo] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const { loading, setLoading } = useLoading();

  const handleModalClose = () =>
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/;
    if (formData.companyEmail && !emailRegex.test(formData.companyEmail))
      newErrors.companyEmail = "Invalid email format";
    if (formData.companyWebsite && !websiteRegex.test(formData.companyWebsite))
      newErrors.companyWebsite = "Invalid website URL format";
    return newErrors;
  };

  const checkIfFormModified = (currentData, initialData) => {
    if (!initialData) return false;
    return Object.keys(currentData).some(
      (key) => currentData[key] !== initialData[key]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value };
      setIsFormModified(checkIfFormModified(newFormData, initialFormData));
      return newFormData;
    });
    const newErrors = validateForm();
    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      setSubmitted(true);
      setLoading(true);
      try {
        const amtplUserID = userInfo[0].AMTPLUserID;
        const body = {
          target: "updateAMTPLUser",
          updateAMTPLUserInput: {
            AMTPLUserID: amtplUserID,
            EmailID: userInfo[0].EmailID,
            FirstName: userInfo[0].FirstName || "",
            LastName: userInfo[0].LastName || "",
            Gender: userInfo[0].Gender || "",
            Country: userInfo[0].Country || "",
            State: userInfo[0].State || "",
            ZipCode: userInfo[0].ZipCode || "",
            Phone1: userInfo[0].Phone1 || "",
            Phone2: userInfo[0].Phone2 || "",
            Address1: userInfo[0].Address1 || "",
            Address2: userInfo[0].Address2 || "",
            CompanyName: formData.companyName || "",
            CompanyEmail: formData.companyEmail || "",
            CompanyWebsite: formData.companyWebsite || "",
            CompanyCountry: formData.companyCountry || "",
            CompanyState: formData.companyState || "",
            CompanyZipCode: formData.companyZipCode || "",
            CompanyPhone1: formData.companyPhone1 || "",
            CompanyPhone2: formData.companyPhone2 || "",
            CompanyAddress1: formData.companyAddress1 || "",
            CompanyAddress2: formData.companyAddress2 || "",
          },
        };
        await postQuery(body);
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Company information updated successfully.",
          autoClose: false,
          nextAction: () => navigate("/seller-farmer/profile"),
        });
      } catch (error) {
        console.error("Submission error:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error!",
          message: error.message || "Failed to submit company information.",
          autoClose: false,
        });
      } finally {
        setSubmitted(false);
        setLoading(false);
      }
    }
  };

  const getUserInfo = useCallback(async (userEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: userEmail },
      });
      setUserInfo(response.items || []);
    } catch (error) {
      console.error("Error loading user info:", error);
      setUserInfo([]);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      setCountryMaster(allCountries.items.length > 0 ? allCountries.items : []);
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        } else {
          setActiveUser(user.attributes.email);
          getUserInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
  }, [navigate, getUserInfo]);

  useEffect(() => {
    loadCountries();
    if (activeUser && userInfo.length > 0) {
      const newFormData = {
        companyName: userInfo[0].CompanyName || "",
        companyEmail: userInfo[0].CompanyEmail || "",
        companyWebsite: userInfo[0].CompanyWebsite || "",
        companyCountry: userInfo[0].CompanyCountry || "",
        companyState: userInfo[0].CompanyState || "",
        companyZipCode: userInfo[0].CompanyZipCode || "",
        companyPhone1: userInfo[0].CompanyPhone1 || "",
        companyPhone2: userInfo[0].CompanyPhone2 || "",
        companyAddress1: userInfo[0].CompanyAddress1 || "",
        companyAddress2: userInfo[0].CompanyAddress2 || "",
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
      setIsFormModified(false);
    }
  }, [activeUser, userInfo, loadCountries]);

  return (
    <div className="container mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-4">
        <div className="table-header">
          <div className="header-text">
            <div className="head-text">Company Information</div>
          </div>
          <div className="head-btn">
            <div className="submit-btn-form">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                form="questionForm"
                onClick={handleSubmit}
              >
                {submitted || loading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>
        <form id="questionForm" className="row g-2">
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Company Name</Typography>
              <TextField
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Company Name"
                error={!!errors.companyName}
                helperText={errors.companyName}
              />
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Company Email</Typography>
              <TextField
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Company Email"
                error={!!errors.companyEmail}
                helperText={errors.companyEmail}
              />
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Company Website</Typography>
              <TextField
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Company Website"
                error={!!errors.companyWebsite}
                helperText={errors.companyWebsite}
              />
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Company Country</Typography>
              <Select
                name="companyCountry"
                value={formData.companyCountry}
                onChange={handleChange}
                displayEmpty
                sx={{ backgroundColor: "white" }}
                error={!!errors.companyCountry}
              >
                <MenuItem value="" disabled>
                  Select country
                </MenuItem>
                {countrymaster.map((p) => (
                  <MenuItem key={p.ID} value={p.Value}>
                    {p.Value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Company State</Typography>
              <TextField
                name="companyState"
                value={formData.companyState}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Company State"
              />
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">PIN/ZIP</Typography>
              <TextField
                name="companyZipCode"
                value={formData.companyZipCode}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="PIN/ZIP"
              />
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Working Phone</Typography>
              <TextField
                name="companyPhone1"
                value={formData.companyPhone1}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Working Phone"
              />
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl fullWidth>
              <Typography variant="body2">Office Phone</Typography>
              <TextField
                name="companyPhone2"
                value={formData.companyPhone2}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Office Phone"
              />
            </FormControl>
          </div>
          <div className="col-md-6">
            <FormControl fullWidth>
              <Typography variant="body2">Address 1</Typography>
              <TextField
                name="companyAddress1"
                value={formData.companyAddress1}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Address"
              />
            </FormControl>
          </div>
          <div className="col-md-6">
            <FormControl fullWidth>
              <Typography variant="body2">Address 2</Typography>
              <TextField
                name="companyAddress2"
                value={formData.companyAddress2}
                onChange={handleChange}
                size="small"
                sx={{ backgroundColor: "white" }}
                placeholder="Address"
              />
            </FormControl>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyInformation;
